/**
 * candidateGrid.ts — the SHARED, pure definition of a candidate property's GRID
 * ANALYSIS status (AD-048 L1A multi-property staff side).
 *
 * Used by BOTH StaffTechnicalPanel (the per-candidate grid checklist) and
 * ComposerDeliveryPanel (the generate-subset gating), so staff and the report
 * agree on one definition. No Firebase, no React.
 *
 * TWO THRESHOLDS (owner-ruled 2026-06-26):
 *   · PROCEED-ABLE  = closed grid-able boundary + ≥1 entrance marked.
 *       AUOs are NOT required to proceed — staff may move ahead (generate) before
 *       finishing every AUO (AUOs are slow). This is the threshold that ENABLES
 *       Generate (revised ADR-4): a candidate is INCLUDED in the report once it
 *       is proceed-able.
 *   · COMPLETE      = proceed-able + every AUO mark resolved (no 'proposed' left;
 *       legacy no-status fixtures = confirmed, matching the loadInputs compose
 *       seam). "Fully gridded."
 *
 *   candidate 'complete'     = ≥1 floor AND every floor complete.
 *   candidate 'partial'      = not complete but ≥1 floor proceed-able (usable now,
 *                              boundary+entrance done, AUOs maybe pending).
 *   candidate 'not_started'  = no floor is proceed-able yet.
 *
 * The rotate/crop step (LOCKED crop tool) is a prerequisite to boundary marking,
 * reachable per candidate via the floor tracer — not a separately gated state.
 */
import { isValidBoundary } from './vastuGrid';

/** Structural shape — accepts the form `PropertyInput`/`FloorInput` AND the
 *  persisted `answers.properties[i]`/`answers.floors[i]` (loose, either source). */
export interface GriddableFloor {
  boundary?: unknown;
  entrances?: unknown[] | null;
  doors?: unknown[] | null;
  fixtures?: Array<{ status?: string } | null | undefined> | null;
}
export interface GriddableProperty {
  /** churn-prone candidate SLOT id (`prop_…`). */
  id?: string;
  /** STABLE registry property id (`P#####`) once the candidate is joined. The
   *  verdict winner (`recommended_property_id`) now keys on THIS (gap #5). */
  propertyId?: string;
  label?: string;
  floors?: GriddableFloor[] | null;
}

export type GridStatus = 'complete' | 'partial' | 'not_started';

/** Closed, grid-able outline (≥3 pts). */
export function floorHasBoundary(fl: GriddableFloor | null | undefined): boolean {
  return !!fl && isValidBoundary(fl.boundary);
}

/** ≥1 entrance marked (a derived engine-facing entrance OR a placed door). */
export function floorHasEntrance(fl: GriddableFloor | null | undefined): boolean {
  if (!fl) return false;
  const e = Array.isArray(fl.entrances) ? fl.entrances.length : 0;
  const d = Array.isArray(fl.doors) ? fl.doors.length : 0;
  return e > 0 || d > 0;
}

/** Every AUO mark resolved (no 'proposed' awaiting staff confirm). */
export function floorAuoResolved(fl: GriddableFloor | null | undefined): boolean {
  const fx = Array.isArray(fl?.fixtures) ? fl!.fixtures! : [];
  return fx.every(
    (f) => !f || f.status === undefined || f.status === 'confirmed' || f.status === 'rejected',
  );
}

/** PROCEED-ABLE = boundary + ≥1 entrance (AUOs NOT required). Enables Generate. */
export function floorProceedable(fl: GriddableFloor | null | undefined): boolean {
  return floorHasBoundary(fl) && floorHasEntrance(fl);
}

/** COMPLETE = proceed-able + all AUOs resolved ("fully gridded"). */
export function floorComplete(fl: GriddableFloor | null | undefined): boolean {
  return floorProceedable(fl) && floorAuoResolved(fl);
}

/** Candidate-level grid status. */
export function candidateGridStatus(p: GriddableProperty | null | undefined): GridStatus {
  const floors = Array.isArray(p?.floors) ? (p!.floors as GriddableFloor[]) : [];
  if (!floors.length) return 'not_started';
  if (floors.every(floorComplete)) return 'complete';
  if (floors.some(floorProceedable)) return 'partial';
  return 'not_started';
}

/** A candidate is includable in the report once ≥1 floor is proceed-able. */
export function candidateProceedable(p: GriddableProperty | null | undefined): boolean {
  const s = candidateGridStatus(p);
  return s === 'complete' || s === 'partial';
}

export interface GridSummary {
  total: number;
  complete: number;
  partial: number;
  notStarted: number;
  /** proceed-able candidates (complete + partial) → included in the report. */
  proceedable: number;
  /** ≥1 proceed-able candidate → Generate is enabled (ADR-4). */
  readyToGenerate: boolean;
  /** ≥2 proceed-able candidates → the report is a COMPARISON. */
  isComparison: boolean;
  /** ids of the proceed-able candidates that WILL be included. */
  includedIds: string[];
  /** ids of candidates not yet proceed-able → excluded/pending. */
  pendingIds: string[];
  /** the recommended pick resolves to a PROCEED-ABLE candidate (ADR-7). */
  recommendedValid: boolean;
}

/** Roll up a candidate list into the generate-gating summary (ADR-4 subset). */
export function gridSummary(
  props: GriddableProperty[] | null | undefined,
  recommendedPropId?: string,
): GridSummary {
  const list = Array.isArray(props) ? props : [];
  const includedIds: string[] = [];
  const pendingIds: string[] = [];
  let complete = 0;
  let partial = 0;
  let notStarted = 0;
  for (const p of list) {
    const s = candidateGridStatus(p);
    if (s === 'complete') {
      complete += 1;
      if (p.id) includedIds.push(p.id);
    } else if (s === 'partial') {
      partial += 1;
      if (p.id) includedIds.push(p.id);
    } else {
      notStarted += 1;
      if (p.id) pendingIds.push(p.id);
    }
  }
  const proceedable = complete + partial;
  // The winner now keys on the STABLE `propertyId` (`P#####`), but stay
  // back-compat with legacy values stored as the churn-prone candidate slot `id`
  // (`prop_…`). Either resolves the same candidate; it must still be proceed-able.
  const recommendedValid =
    !!recommendedPropId &&
    list.some(
      (p) =>
        (recommendedPropId === p.propertyId || recommendedPropId === p.id) &&
        candidateProceedable(p),
    );
  return {
    total: list.length,
    complete,
    partial,
    notStarted,
    proceedable,
    readyToGenerate: proceedable >= 1,
    isComparison: proceedable >= 2,
    includedIds,
    pendingIds,
    recommendedValid,
  };
}

/** Short label + colour intent for a candidate's grid pill. */
export function gridBadge(status: GridStatus): { label: string; intent: 'good' | 'warn' | 'mute' } {
  if (status === 'complete') return { label: '✓ grid complete', intent: 'good' };
  if (status === 'partial') return { label: 'partial', intent: 'warn' };
  return { label: 'not started', intent: 'mute' };
}
